<template>
  <modal
    :show="show"
    :modifiers="['rounded-border']"
    class="before-you-go-modal"
    @close="$emit('close')"
  >
    <div class="before-you-go-modal__content p-6 md:p-10 max-w-lg md:max-w-4xl lg:max-w-5xl">
      <h3 class="text-h3 font-bold text-midnight mb-6 text-center" v-html="$t('headline')" />

      <div class="md:flex md:items-start md:gap-10">
        <div class="w-full md:w-1/2 md:flex-shrink-0 mb-6 md:mb-0">
          <div class="relative">
            <video
              ref="video"
              class="rounded-lg w-full cursor-pointer"
              :poster="VIDEO.poster"
              autoplay
              muted
              loop
              playsinline
              preload="metadata"
              @play="onVideoPlay"
              @click="onVideoClick"
            >
              <source :src="VIDEO.webm" type="video/webm" />
              <source :src="VIDEO.mp4" type="video/mp4" />
            </video>

            <button
              v-if="isMuted"
              type="button"
              class="absolute bottom-3 left-3 flex items-center gap-1 bg-midnight/70 text-white text-p2 font-bold rounded-full py-1 px-3 cursor-pointer"
              @click="onVideoClick"
            >
              🔊 {{ $t("tapForSound") }}
            </button>
          </div>

          <p class="hidden md:block text-xs text-midnight mt-3 text-center">
            {{ $t("supportText") }}
            <button type="button" class="text-xs font-bold underline hover:text-raspberry cursor-pointer" @click="$emit('contact-support')">
              {{ $t("supportLink") }}
            </button>
          </p>
        </div>

        <div class="text-center md:text-left">
          <p class="text-p1 text-midnight mb-6" v-html="$t('body')" />

          <p v-if="showTrialNote" class="text-p1 font-bold text-midnight mb-6">
            {{ $t("trialNote", { date: trialChargeDate }) }}
          </p>

          <p v-if="showTenure" class="text-p1 font-bold text-midnight mb-6">
            {{ $t("tenure", { months: membershipMonths }) }}
          </p>

          <ul class="before-you-go-modal__benefits text-left bg-pale-mint-grey rounded-lg p-6 mb-8">
            <li v-for="(benefit, index) in benefits" :key="index" class="flex gap-2 text-p1 text-midnight mb-2 last:mb-0">
              <span class="flex-shrink-0" aria-hidden="true">{{ benefit.icon }}</span>
              <span>{{ benefit.label }}</span>
            </li>
          </ul>

          <button
            type="button"
            class="block w-full font-bold text-white bg-raspberry hover:bg-raspberry2 rounded-lg py-3 px-6 mb-3 cursor-pointer"
            @click="$emit('keep')"
          >
            {{ $t("keep") }}
          </button>

          <button
            type="button"
            class="block w-full font-bold text-midnight bg-white border border-solid border-midnight hover:border-raspberry hover:text-raspberry rounded-lg py-3 px-6 cursor-pointer"
            @click="$emit('continue')"
          >
            {{ $t("continue") }}
          </button>

          <p class="text-xs text-midnight text-center mt-3">{{ $t("continueHint") }}</p>
        </div>
      </div>

      <p class="md:hidden text-xs text-midnight mt-6 text-center">
        {{ $t("supportText") }}
        <button type="button" class="text-xs font-bold underline hover:text-raspberry cursor-pointer" @click="$emit('contact-support')">
          {{ $t("supportLink") }}
        </button>
      </p>
    </div>
  </modal>
</template>

<script>
import Modal from "@/shared/modal"

const CDN_BASE = "https://static.blinkist.com/web-growth/pricing/retention-offer"
// Audio-bearing renditions so the clip can be unmuted. Autoplay starts muted (browser
// policy); clicking the video unmutes it and replays from the start (a user gesture).
const VIDEO = {
  webm: `${CDN_BASE}/retention-offer-web.webm`,
  mp4: `${CDN_BASE}/retention-offer-web.mp4`,
  poster: `${CDN_BASE}/retention-offer-poster.jpg`
}

export default {
  name: "BeforeYouGoModal",
  components: {
    Modal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    membershipMonths: {
      type: Number,
      default: 0
    },
    isTrialing: {
      type: Boolean,
      default: false
    },
    trialChargeDate: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      VIDEO,
      isMuted: true,
      videoPlayTracked: false
    }
  },
  computed: {
    showTrialNote() {
      // trialing users haven't paid yet, so reassure them they keep free access until the charge date
      return this.isTrialing && this.trialChargeDate.length > 0
    },
    showTenure() {
      // only worth mentioning once it's a meaningful stretch of time
      return this.membershipMonths >= 3
    },
    benefits() {
      // split the leading emoji from the text so wrapped lines hang-indent next to it
      return [this.$t("benefitLibrary"), this.$t("benefitSaved"), this.$t("benefitInfographics"), this.$t("benefitRecommendations")].map(
        (benefit) => {
          const [icon, ...rest] = benefit.split(" ")
          return { icon, label: rest.join(" ") }
        }
      )
    }
  },
  methods: {
    onVideoPlay() {
      // play can fire more than once (loop / replay) so only report the first one
      if (this.videoPlayTracked) return
      this.videoPlayTracked = true
      this.$emit("video-played")
    },
    onVideoClick() {
      const video = this.$refs.video
      if (!video) return

      if (this.isMuted) {
        // first click: turn sound on and restart from the beginning
        video.muted = false
        video.loop = false
        video.currentTime = 0
        this.isMuted = false
      } else if (video.paused) {
        video.play()
        return
      } else {
        video.pause()
        return
      }

      const playback = video.play()
      if (playback && typeof playback.catch === "function") playback.catch(() => {})
    }
  },
  i18n: {
    messages: {
      en: {
        headline: "Wait! Before you go…",
        body: "You started Blinkist to grow, to think sharper and become your best self. Have you gotten there yet? Stay and keep going. Here's what you'd leave behind:",
        benefitLibrary: "📚 9,000+ bestselling titles, explained in 15 minutes",
        benefitSaved: "🔖 All your saved Blinks and highlights",
        benefitInfographics: "📊 Blinkist Infographics",
        benefitRecommendations: "✨ Personalized recommendations picked just for you",
        trialNote: "Good news! You won't be charged until {date}. Stay and keep learning for free until then. You can still cancel anytime before.",
        tenure: "You've been growing with Blinkist for {months} months. Don't stop now.",
        supportText: "Something not working as expected?",
        supportLink: "Contact our support team",
        tapForSound: "Tap for sound",
        keep: "Keep my subscription",
        continue: "No thanks, continue to cancel",
        continueHint: "Nothing is cancelled yet. You'll finish on the next page."
      },
      de: {
        headline: "Warte! Bevor du gehst…",
        body: "Du hast mit Blinkist angefangen, um zu wachsen, um klarer zu denken und über dich hinauszuwachsen. Hast du das schon erreicht? Bleib dran. Das würdest du zurücklassen:",
        benefitLibrary: "📚 Über 9.000 Bestseller, erklärt in 15 Minuten",
        benefitSaved: "🔖 Alle deine gespeicherten Blinks und Markierungen",
        benefitInfographics: "📊 Blinkist Infografiken",
        benefitRecommendations: "✨ Personalisierte Empfehlungen nur für dich",
        trialNote: "Gute Nachrichten! Du wirst erst am {date} belastet. Bleib dabei und lerne bis dahin kostenlos weiter. Du kannst jederzeit vorher kündigen.",
        tenure: "Du wächst schon seit {months} Monaten mit Blinkist. Hör jetzt nicht auf.",
        supportText: "Funktioniert etwas nicht wie erwartet?",
        supportLink: "Kontaktiere unser Support-Team",
        tapForSound: "Für Ton tippen",
        keep: "Abo behalten",
        continue: "Nein danke, weiter zur Kündigung",
        continueHint: "Es wird noch nichts gekündigt. Du schließt den Vorgang auf der nächsten Seite ab."
      },
      es: {
        headline: "¡Espera! Antes de irte…",
        body: "Empezaste Blinkist para crecer, para pensar mejor y convertirte en tu mejor versión. ¿Ya lo has conseguido? Quédate y sigue avanzando. Esto es lo que dejarías atrás:",
        benefitLibrary: "📚 Más de 9.000 títulos superventas, explicados en 15 minutos",
        benefitSaved: "🔖 Todos tus Blinks guardados y tus notas",
        benefitInfographics: "📊 Infografías de Blinkist",
        benefitRecommendations: "✨ Recomendaciones personalizadas elegidas para ti",
        trialNote: "Buenas noticias: no se te cobrará hasta el {date}. Quédate y sigue aprendiendo gratis hasta entonces. Puedes cancelar en cualquier momento antes.",
        tenure: "Llevas {months} meses creciendo con Blinkist. No pares ahora.",
        supportText: "¿Algo no funciona como esperabas?",
        supportLink: "Contacta con nuestro equipo de soporte",
        tapForSound: "Toca para activar el sonido",
        keep: "Mantener mi suscripción",
        continue: "No, gracias, continuar con la cancelación",
        continueHint: "Todavía no se ha cancelado nada. Lo completarás en la página siguiente."
      },
      pt: {
        headline: "Espere! Antes de ir embora…",
        body: "Você começou no Blinkist para crescer, pensar com mais clareza e se tornar sua melhor versão. Você já chegou lá? Fique e continue evoluindo. É isso que você deixaria para trás:",
        benefitLibrary: "📚 Mais de 9.000 títulos de sucesso, explicados em 15 minutos",
        benefitSaved: "🔖 Todos os seus Blinks salvos e destaques",
        benefitInfographics: "📊 Infografias da Blinkist",
        benefitRecommendations: "✨ Recomendações personalizadas escolhidas para você",
        trialNote: "Boa notícia: você só será cobrado em {date}. Fique e continue aprendendo de graça até lá. Você ainda pode cancelar a qualquer momento antes.",
        tenure: "Você está crescendo com a Blinkist há {months} meses. Não pare agora.",
        supportText: "Algo não está funcionando como esperado?",
        supportLink: "Fale com nossa equipe de suporte",
        tapForSound: "Toque para ativar o som",
        keep: "Manter minha assinatura",
        continue: "Não, obrigado, continuar o cancelamento",
        continueHint: "Nada foi cancelado ainda. Você concluirá na próxima página."
      }
    }
  }
}
</script>
